import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { REGISTER } from "@/constants/testIds";
import AuthLayout from "@/components/AuthLayout";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { formatApiError } from "@/lib/api";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password !== form.confirm) {
            setError("Passwords do not match");
            return;
        }
        if (form.password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            await register(form.name, form.email, form.password);
            navigate("/profile/setup");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title="Begin your sacred journey" subtitle="Create Account">
            <GoogleLoginButton />
            <div className="my-6 flex items-center gap-4 text-xs tracking-[0.2em] uppercase text-navy-400">
                <div className="h-px flex-1 bg-navy-100"></div>
                <span>or</span>
                <div className="h-px flex-1 bg-navy-100"></div>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs tracking-[0.18em] uppercase text-navy-500 font-semibold mb-2">Full Name</label>
                    <input
                        type="text" required
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid={REGISTER.nameInput}
                        className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all"
                        placeholder="Your name"
                    />
                </div>
                <div>
                    <label className="block text-xs tracking-[0.18em] uppercase text-navy-500 font-semibold mb-2">Email</label>
                    <input
                        type="email" required
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid={REGISTER.emailInput}
                        className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all"
                        placeholder="you@example.com"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs tracking-[0.18em] uppercase text-navy-500 font-semibold mb-2">Password</label>
                        <input
                            type="password" required minLength={6}
                            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                            data-testid={REGISTER.passwordInput}
                            className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all"
                            placeholder="6+ chars"
                        />
                    </div>
                    <div>
                        <label className="block text-xs tracking-[0.18em] uppercase text-navy-500 font-semibold mb-2">Confirm</label>
                        <input
                            type="password" required
                            value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                            data-testid={REGISTER.passwordConfirmInput}
                            className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all"
                            placeholder="Confirm"
                        />
                    </div>
                </div>
                {error && <div className="text-sm text-red-600 font-sans">{error}</div>}
                <button
                    type="submit" disabled={loading}
                    data-testid={REGISTER.submitButton}
                    className="w-full font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy rounded-lg px-6 py-4 hover:bg-gold-600 hover:text-white active:scale-95 transition-all shadow-lg shadow-gold/30 disabled:opacity-50"
                >
                    {loading ? "Creating…" : "Create Free Account"}
                </button>
            </form>
            <p className="mt-6 text-sm text-navy-500 font-sans text-center">
                Already a member?{" "}
                <Link to="/login" data-testid={REGISTER.loginLink} className="text-navy font-semibold hover:text-gold-600">Sign in</Link>
            </p>
        </AuthLayout>
    );
}
